import { useMemo, useRef, useState, useEffect } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Field,
  Input,
  Switch,
  Text,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import SignaturePreview from "../components/SignaturePreview";
import SignatureEditor from "../components/SignatureEditor";

const STEPS = ["Name", "Senders", "Recipients", "Scheduler", "Logic", "Design"];

const useStyles = makeStyles({
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "flex-end", zIndex: 1000 },
  panel: { width: "min(1240px, 96vw)", maxWidth: "100%", height: "100%", background: "#fff", display: "flex", flexDirection: "column" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2) },
  bodyWrap: { flexGrow: 1, display: "flex", minHeight: 0 },
  rail: { width: "180px", flexShrink: 0, ...shorthands.borderRight("1px", "solid", tokens.colorNeutralStroke2), padding: "12px 0" },
  railItem: { padding: "10px 20px", cursor: "pointer", color: tokens.colorNeutralForeground2, ...shorthands.borderLeft("3px", "solid", "transparent") },
  railActive: { color: "#0f2e1d", fontWeight: tokens.fontWeightSemibold, ...shorthands.borderLeft("3px", "solid", "#00833f"), background: "#f1f7f3" },
  railDone: { color: "#00833f" },
  content: { flexGrow: 1, overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: "14px" },
  footer: { display: "flex", justifyContent: "space-between", padding: "14px 20px", ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2) },
  footerRight: { display: "flex", gap: "8px" },
  row: { display: "flex", gap: "12px", flexWrap: "wrap" },
  grow: { flexGrow: 1, minWidth: "200px" },
  code: { fontFamily: "Consolas, monospace", fontSize: "12px" },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chip: { fontSize: "11px", padding: "3px 8px", borderRadius: "12px", border: `1px solid ${tokens.colorNeutralStroke2}`, background: "#f3f6f4", cursor: "pointer" },
  hint: { color: tokens.colorNeutralForeground3 },
});

const listVal = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const dtLocal = (v) => (v || "").slice(0, 16);

export default function RuleWizard({ index, assignments, templates, onApply, onPublish, onClose }) {
  const styles = useStyles();
  const isNew = index === -1;
  const templateList = templates.templates || [];

  // Build the initial draft (rule fields + the design HTML of its template).
  const initial = useMemo(() => {
    const base = isNew
      ? { name: "New rule", templateId: templates.defaultTemplateId || templateList[0]?.id, conditions: {}, schedule: {}, enabled: true }
      : JSON.parse(JSON.stringify(assignments.rules[index]));
    const tpl = templateList.find((t) => t.id === base.templateId) || templateList[0];
    return {
      ...base,
      templateId: tpl?.id,
      designName: tpl?.name || "Signature",
      designHtml: tpl?.html || "",
      makeDefault: templates.defaultTemplateId === base.templateId,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [draft, setDraft] = useState(initial);
  const [step, setStep] = useState(0);
  const [debouncedHtml, setDebouncedHtml] = useState(initial.designHtml);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedHtml(draft.designHtml), 500);
    return () => clearTimeout(id);
  }, [draft.designHtml]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setCond = (key, val) => {
    const conditions = { ...(draft.conditions || {}) };
    if (val === "" || val == null || (Array.isArray(val) && val.length === 0)) delete conditions[key];
    else conditions[key] = val;
    set({ conditions });
  };
  const setSchedule = (patch) => set({ schedule: { ...(draft.schedule || {}), ...patch } });

  // Commit the draft into config shape, returning [nextAssignments, nextTemplates].
  const build = () => {
    const tplId = draft.templateId || `template-${Date.now()}`;
    let nextTemplates = { ...templates, templates: [...templateList] };
    const tIdx = nextTemplates.templates.findIndex((t) => t.id === tplId);
    const tplObj = { id: tplId, name: draft.designName, description: tIdx >= 0 ? nextTemplates.templates[tIdx].description : "", file: tIdx >= 0 ? nextTemplates.templates[tIdx].file : "", html: draft.designHtml };
    if (tIdx >= 0) nextTemplates.templates[tIdx] = tplObj;
    else nextTemplates.templates.push(tplObj);
    if (draft.makeDefault) nextTemplates.defaultTemplateId = tplId;

    const rule = { name: draft.name, templateId: tplId, conditions: draft.conditions || {}, schedule: draft.schedule || {}, enabled: draft.enabled !== false };
    const rules = [...(assignments.rules || [])];
    if (isNew) rules.push(rule);
    else rules[index] = rule;
    return [{ ...assignments, rules }, nextTemplates];
  };

  const save = (publish) => {
    const [nextAssignments, nextTemplates] = build();
    if (publish) onPublish(nextAssignments, nextTemplates);
    else onApply(nextAssignments, nextTemplates);
    onClose();
  };

  const c = draft.conditions || {};
  const s = draft.schedule || {};

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <Text size={500} weight="semibold">{isNew ? "Create rule" : `Edit: ${draft.name}`}</Text>
          <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} />
        </div>

        <div className={styles.bodyWrap}>
          <div className={styles.rail}>
            {STEPS.map((label, i) => (
              <div
                key={label}
                className={`${styles.railItem} ${i === step ? styles.railActive : i < step ? styles.railDone : ""}`}
                onClick={() => setStep(i)}
              >
                {i + 1}. {label}
              </div>
            ))}
          </div>

          <div className={styles.content}>
            {step === 0 && (
              <>
                <Field label="Rule name"><Input value={draft.name} onChange={(_, d) => set({ name: d.value })} /></Field>
                <Switch label="Rule enabled" checked={draft.enabled !== false} onChange={(_, d) => set({ enabled: d.checked })} />
              </>
            )}

            {step === 1 && (
              <>
                <Text className={styles.hint}>Which senders this rule applies to. Leave all blank to match everyone.</Text>
                <Field label="Department(s), comma-separated"><Input value={listVal(c.department)} onChange={(_, d) => setCond("department", toList(d.value))} /></Field>
                <Field label="Group(s), comma-separated"><Input value={listVal(c.group)} onChange={(_, d) => setCond("group", toList(d.value))} /></Field>
                <Field label="Specific user email(s), comma-separated"><Input value={listVal(c.userEmail)} onChange={(_, d) => setCond("userEmail", toList(d.value))} /></Field>
                <Field label="Job title contains"><Input value={c.jobTitleContains || ""} onChange={(_, d) => setCond("jobTitleContains", d.value)} /></Field>
              </>
            )}

            {step === 2 && (
              <>
                <Text className={styles.hint}>Apply based on who the message is going to.</Text>
                <Field label="Recipient audience">
                  <Dropdown
                    selectedOptions={[c.audience || "any"]}
                    onOptionSelect={(_, d) => setCond("audience", d.optionValue === "any" ? "" : d.optionValue)}
                  >
                    <Option value="any">Any recipient</Option>
                    <Option value="internal">Internal only</Option>
                    <Option value="external">External only</Option>
                  </Dropdown>
                </Field>
              </>
            )}

            {step === 3 && (
              <>
                <Text className={styles.hint}>Optional active window. Leave blank for always-on. Outside this window the rule is skipped.</Text>
                <div className={styles.row}>
                  <Field label="Starts"><Input type="datetime-local" value={dtLocal(s.start)} onChange={(_, d) => setSchedule({ start: d.value })} /></Field>
                  <Field label="Ends"><Input type="datetime-local" value={dtLocal(s.end)} onChange={(_, d) => setSchedule({ end: d.value })} /></Field>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <Field label="Apply to item type">
                  <Dropdown
                    selectedOptions={[c.itemType || "any"]}
                    onOptionSelect={(_, d) => setCond("itemType", d.optionValue === "any" ? "" : d.optionValue)}
                  >
                    <Option value="any">Messages & appointments</Option>
                    <Option value="message">Messages only</Option>
                    <Option value="appointment">Appointments only</Option>
                  </Dropdown>
                </Field>
                <Switch
                  label="Use this signature as the default (when no rule matches)"
                  checked={!!draft.makeDefault}
                  onChange={(_, d) => set({ makeDefault: d.checked })}
                />
              </>
            )}

            {step === 5 && (
              <>
                <div className={styles.row}>
                  <Field label="Signature template" className={styles.grow}>
                    <Dropdown
                      selectedOptions={[draft.templateId]}
                      value={draft.designName}
                      onOptionSelect={(_, d) => {
                        if (d.optionValue === "__new__") {
                          set({ templateId: `template-${Date.now()}`, designName: "New signature", designHtml: '<table style="font-family:Arial;font-size:10pt;"><tr><td style="font-weight:bold;color:{{brandColor}};">{{displayName}}</td></tr><tr><td>{{companyName}}</td></tr></table>' });
                        } else {
                          const t = templateList.find((x) => x.id === d.optionValue);
                          if (t) set({ templateId: t.id, designName: t.name, designHtml: t.html });
                        }
                      }}
                    >
                      {templateList.map((t) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                      <Option value="__new__">➕ New template…</Option>
                    </Dropdown>
                  </Field>
                  <Field label="Template name" className={styles.grow}>
                    <Input value={draft.designName} onChange={(_, d) => set({ designName: d.value })} />
                  </Field>
                </div>

                <SignatureEditor
                  value={draft.designHtml}
                  onChange={(html) => set({ designHtml: html })}
                  templates={templateList}
                  onOpenTemplate={(t) => set({ templateId: t.id, designName: t.name, designHtml: t.html })}
                  onSaveAsTemplate={(html) => {
                    const name = window.prompt("New template name", `${draft.designName} (copy)`);
                    if (name) set({ templateId: `template-${Date.now()}`, designName: name, designHtml: html });
                  }}
                />

                <div>
                  <Text size={200} className={styles.hint}>Preview with sample data (placeholders filled)</Text>
                  <SignaturePreview html={debouncedHtml} width="100%" />
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <Button appearance="secondary" onClick={onClose}>Cancel</Button>
          <div className={styles.footerRight}>
            <Button appearance="secondary" disabled={step === 0} onClick={() => setStep((x) => x - 1)}>Back</Button>
            {step < STEPS.length - 1 ? (
              <Button appearance="primary" onClick={() => setStep((x) => x + 1)}>Next</Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={() => save(false)}>Save draft</Button>
                <Button appearance="primary" onClick={() => save(true)}>Save &amp; Publish</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
